import { useEffect, useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, ChevronDown, X, Globe2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FIRM, NAV_LINKS, PRACTICE_AREAS_NAV } from "@/constants/firmInfo";
import { DynamicIcon } from "@/components/shared/IconMap";
import { CTAButton } from "@/components/shared/CTAButton";
import { MobileNav } from "@/components/layout/MobileNav";
import { useLanguage } from "@/i18n/LanguageContext";
import { PRACTICE_AREAS_ES } from "@/i18n/translations";

// Maps each nav route to its translation key so labels stay in sync with
// NAV_LINKS without duplicating route data inside the translation dictionary.
const NAV_KEY_BY_PATH = {
  "/": "nav.home",
  "/about": "nav.about",
  "/case-results": "nav.caseResults",
  "/insights": "nav.insights",
  "/contact": "nav.contact",
};

// Defined outside Header so it isn't recreated on every render (avoids the
// "unstable nested component" lint warning and unnecessary DOM churn).
const LanguageToggle = ({ isSpanish, toggleLanguage, className = "", testId = "language-toggle-button" }) => (
  <button
    type="button"
    onClick={toggleLanguage}
    className={`flex items-center gap-1.5 text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--gold-500)] rounded-full ${className}`}
    data-testid={testId}
    aria-label={isSpanish ? "Switch to English" : "Cambiar a Español"}
  >
    <Globe2 className="h-4 w-4 text-[color:var(--gold-500)] shrink-0" />
    <span className={!isSpanish ? "text-[color:var(--gold-400)] font-semibold" : "text-white/60"}>EN</span>
    <span className="text-white/30">/</span>
    <span className={isSpanish ? "text-[color:var(--gold-400)] font-semibold" : "text-white/60"}>ES</span>
  </button>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [utilityBarOpen, setUtilityBarOpen] = useState(true);
  const { t, isSpanish, toggleLanguage } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // NAV_LINKS is a small static constant, but memoizing avoids recomputing
  // (and creating a new array reference) on every render/re-filter pass.
  const secondaryNavLinks = useMemo(() => NAV_LINKS.filter((l) => l.label !== "Home"), []);

  const linkClass = ({ isActive }) =>
    `relative py-1 text-sm font-medium transition-colors after:absolute after:left-0 after:-bottom-1 after:h-[1.5px] after:bg-[color:var(--gold-500)] after:transition-all after:duration-300 ${
      isActive ? "text-[color:var(--gold-400)] after:w-full" : "text-white/90 hover:text-[color:var(--gold-400)] after:w-0 hover:after:w-full"
    }`;

  const practiceAreaTitle = (area) => (isSpanish ? PRACTICE_AREAS_ES[area.slug]?.title || area.title : area.title);

  return (
    <div data-testid="site-header-wrapper">
      {utilityBarOpen && (
        <div className="bg-[color:var(--gold-500)] text-[color:var(--navy-950)]" data-testid="utility-announcement-bar">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center justify-center gap-2 text-center relative">
            <Globe2 className="h-3.5 w-3.5 shrink-0" />
            <p className="text-[11px] sm:text-xs font-semibold tracking-wide" data-testid="utility-bar-text">
              {t("utilityBar")}
            </p>
            <button
              onClick={() => setUtilityBarOpen(false)}
              className="absolute right-3 sm:right-6 p-1 hover:opacity-70"
              aria-label="Dismiss announcement"
              data-testid="utility-bar-dismiss-button"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 w-full border-b border-white/10 bg-[rgba(11,18,32,0.97)] backdrop-blur supports-[backdrop-filter]:bg-[rgba(11,18,32,0.92)] transition-all duration-200 ${
          scrolled ? "py-2 shadow-[0_10px_30px_rgba(0,0,0,0.3)]" : "py-3"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo-link" aria-label={FIRM.name}>
            <img
              src={FIRM.logoSeal}
              alt={`${FIRM.name} seal logo`}
              className="h-11 w-11 object-contain"
              loading="eager"
            />
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-heading text-white text-lg sm:text-xl tracking-wide">RINNOU &amp; ASSOCIATES</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--gold-400)]">A Professional Corporation</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            <NavLink to="/" className={linkClass} end data-testid="nav-link-home">
              {t("nav.home")}
            </NavLink>

            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-white/90 hover:text-[color:var(--gold-400)] transition-colors outline-none" data-testid="nav-practice-areas-trigger">
                {t("nav.practiceAreas")} <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-72 bg-white" align="start">
                {PRACTICE_AREAS_NAV.map((area) => (
                  <DropdownMenuItem key={area.slug} asChild>
                    <Link
                      to={`/practice-areas/${area.slug}`}
                      className="flex items-center gap-3 cursor-pointer py-2"
                      data-testid={`nav-practice-area-${area.slug}`}
                    >
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[color:var(--gold-500)]/12">
                        <DynamicIcon name={area.icon} className="h-3.5 w-3.5 text-[color:var(--navy-950)]" />
                      </span>
                      <span>{practiceAreaTitle(area)}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {secondaryNavLinks.map((link) => (
              <NavLink key={link.to} to={link.to} className={linkClass} data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {t(NAV_KEY_BY_PATH[link.to] || "nav.home", link.label)}
              </NavLink>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="hidden lg:flex items-center gap-5">
            <LanguageToggle isSpanish={isSpanish} toggleLanguage={toggleLanguage} />
            <a
              href={`tel:${FIRM.phoneTel}`}
              className="flex items-center gap-2 text-sm font-semibold text-white hover:text-[color:var(--gold-400)] transition-colors"
              data-testid="header-phone-link"
            >
              <Phone className="h-4 w-4 text-[color:var(--gold-500)]" />
              {FIRM.phoneDisplay}
            </a>
            <Link to="/contact" data-testid="header-schedule-consultation-button">
              <CTAButton variant="brass">{t("nav.schedule")}</CTAButton>
            </Link>
          </div>

          {/* Mobile menu */}
          <div className="flex lg:hidden items-center gap-1">
            <LanguageToggle isSpanish={isSpanish} toggleLanguage={toggleLanguage} className="mr-1" testId="mobile-language-toggle-button-top" />
            <a href={`tel:${FIRM.phoneTel}`} className="p-2 text-white" data-testid="header-mobile-phone-link" aria-label="Call us">
              <Phone className="h-5 w-5 text-[color:var(--gold-500)]" />
            </a>
            <MobileNav
              open={mobileOpen}
              onOpenChange={setMobileOpen}
              isSpanish={isSpanish}
              toggleLanguage={toggleLanguage}
              t={t}
              navKeyByPath={NAV_KEY_BY_PATH}
              secondaryNavLinks={secondaryNavLinks}
            />
          </div>
        </div>
      </header>
    </div>
  );
};
